'use client';

import { useAuth } from '@/app/_hooks/use-auth';
import { Spinner } from '@bprogress/next';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

interface AuthGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

export function AuthGuard({ children, fallback }: AuthGuardProps) {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuth();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      // Don't add redirectTo if we're already on an auth page or on root
      const currentPath = location.pathname + location.search;
      const isAuthPage = location.pathname.startsWith('/auth');
      const isRootPath = location.pathname === '/' || location.pathname === '';

      if (isAuthPage || isRootPath) {
        navigate('/auth/login', { replace: true });
      } else {
        const searchParams = new URLSearchParams();
        searchParams.set('redirectTo', currentPath);
        navigate(`/auth/login?${searchParams.toString()}`, { replace: true });
      }
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading)
    return (
      fallback || (
        <div className="flex h-screen w-full items-center justify-center">
          <Spinner className="h-8 w-8" />
        </div>
      )
    );

  if (!isAuthenticated) return null;

  return <>{children}</>;
}

export function GuestGuard({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading, user } = useAuth();

  useEffect(() => {
    if (!isLoading && isAuthenticated) {
      const spend = user?.user_info?.spend ?? 0;
      const target = spend > 0 ? '/dashboard/usage' : '/dashboard/quickstart';
      navigate(target, { replace: true });
    }
  }, [isAuthenticated, isLoading, navigate, user]);

  if (isLoading)
    return (
      <div className="flex h-screen w-full items-center justify-center">
        <Spinner className="h-8 w-8" />
      </div>
    );

  if (isAuthenticated) return null;

  return <>{children}</>;
}
